'use client';

import { memo, useEffect, useMemo, useState } from 'react';
import { InfoModal } from 'app/book/components/ServiceStep/components/ServiceList/components/InfoModal';
import config from 'config';
import { useGetSiteAlertsQuery } from 'services/siteAlert';

import { Box, useDisclosure } from '@chakra-ui/react';

import { useAppDispatch } from 'store/hooks';
import { setSiteAlerts } from 'store/reducers/clientSlice';

import {
  SiteAlertPlacement,
  SiteAlertsInterface,
  SiteAlertTarget,
  SiteAlertType
} from 'types';

import { BannerView } from '../BannerView';

const {
  siteAlert: { desktop_banners }
} = config;

const pollingInterval = 60 * 60 * 1000;

interface AboveSiteBannerProps {
  areaId: number | null;
  siteAlerts: SiteAlertsInterface[];
}

const AboveSiteBanner = ({ areaId, siteAlerts }: AboveSiteBannerProps) => {
  const { data: fetchedSiteAlerts } = useGetSiteAlertsQuery('getSiteAlerts', {
    pollingInterval
  });

  const dispatch = useAppDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedBanner, selectBanner] = useState<SiteAlertsInterface | null>(
    null
  );

  const banners = useMemo(
    () =>
      (siteAlerts ?? [])
        .filter(
          (alert) => alert.site_alert_placement === SiteAlertPlacement.AboveSite
        )
        .filter(
          (alert) =>
            alert.site_alert_target === SiteAlertTarget.Sitewide ||
            alert.site_area.some((area) => area.id === areaId)
        ),
    [siteAlerts, areaId]
  );

  useEffect(() => {
    if (fetchedSiteAlerts) {
      dispatch(setSiteAlerts(fetchedSiteAlerts));
    }
  }, [dispatch, fetchedSiteAlerts]);

  const onBannerClick = (idx: number) => {
    const currentItem = banners[idx];
    selectBanner(currentItem);

    switch (currentItem.site_alert_type) {
      case SiteAlertType.Popup:
        onOpen();
        break;
      case SiteAlertType.PopupLarge:
        onOpen();
        break;
      case SiteAlertType.PopupText:
        onOpen();
        break;
      case SiteAlertType.Url:
        window.location.href = currentItem.url;
        break;
      case SiteAlertType.UrlNewTab:
        window.open(currentItem.url, '_blank');
        break;
      default:
        break;
    }
  };

  const onCloseModal = () => {
    onClose();
    selectBanner(null);
  };

  return (
    <Box
      position='relative'
      w='full'
      sx={{
        '.slick-slider .slick-slide': {
          padding: 0
        }
      }}
    >
      {Boolean(banners.length) && (
        <BannerView banners={banners} onBannerClick={onBannerClick} />
      )}

      <InfoModal
        isOpen={isOpen}
        onClose={onCloseModal}
        title={selectedBanner?.site_alert_cat_name ?? ''}
        subtitle={selectedBanner?.name ?? ''}
        description={selectedBanner?.text ?? ''}
        banners={desktop_banners}
      />
    </Box>
  );
};

export default memo(AboveSiteBanner);
